// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { resolveStoredImageUrl } from './splatImageUpload';
import { getSplatTypeLabel } from '../../domain/splatConstants';

function formatUpdatedAt(splat) {
  if (splat.updatedAt?.toDate) {
    return splat.updatedAt.toDate().toLocaleString();
  }
  if (splat.updatedAt?.__time__) {
    return new Date(splat.updatedAt.__time__).toLocaleString();
  }
  return '';
}

function triggerThresholdDisplay(splat) {
  const th = splat.triggerThreshold;
  if (th == null || th === '') return '—';
  return th;
}

function completionRequiresDisplay(splat, splatIdToName) {
  const ids = splat.completionSplatIds;
  if (!Array.isArray(ids) || !ids.length) return '—';
  return ids
    .map((id) => splatIdToName.get(id) || id)
    .join(', ');
}

function firstEventReviewEventDisplay(splat, eventIdToName) {
  const rawId = String(splat.eventId ?? '').trim();
  if (!rawId) return '—';
  return eventIdToName.get(rawId) || rawId;
}

function DetailRow({ label, children, multiline }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          wordBreak: 'break-word',
          ...(multiline ? { whiteSpace: 'pre-wrap' } : {}),
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SplatImagePreview({ filename, url }) {
  const [loadFailed, setLoadFailed] = useState(false);

  const handleImageError = useCallback(() => {
    setLoadFailed(true);
  }, []);

  useEffect(() => {
    setLoadFailed(false);
  }, [url]);

  const showImg = Boolean(url) && !loadFailed;

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 220,
        height: 220,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        p: 1,
      }}
    >
      {showImg ? (
        <Box
          component="img"
          src={url}
          alt={filename || 'Splat image'}
          referrerPolicy="no-referrer"
          onError={handleImageError}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" align="center">
          {filename
            ? url
              ? 'Could not load preview'
              : 'Preview unavailable'
            : 'No image'}
        </Typography>
      )}
    </Box>
  );
}

function SplatCardHeaderRow({
  splat,
  onEditClick,
  onDeleteClick,
  deleteDisabledReason,
}) {
  const deleteLabel = `Delete splat ${splat.id}`;
  const deleteButton = (
    <IconButton
      aria-label={deleteLabel}
      size="small"
      color="error"
      disabled={Boolean(deleteDisabledReason)}
      onClick={deleteDisabledReason ? undefined : onDeleteClick}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {splat.name ?? '—'}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', wordBreak: 'break-all' }}
        >
          {splat.id}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
        }}
      >
        <Chip
          label={getSplatTypeLabel(splat.type)}
          size="small"
          variant="outlined"
        />
        <Chip
          label={splat.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={splat.isActive ? 'success' : 'default'}
          variant={splat.isActive ? 'filled' : 'outlined'}
        />
        <IconButton
          aria-label={`Edit splat ${splat.id}`}
          size="small"
          onClick={onEditClick}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        {deleteDisabledReason ? (
          <Tooltip title={deleteDisabledReason}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              {deleteButton}
            </Box>
          </Tooltip>
        ) : (
          deleteButton
        )}
      </Box>
    </Box>
  );
}

function SplatCardDetailsGrid({
  splat,
  imageName,
  previewUrl,
  splatIdToName,
  eventIdToName,
}) {
  return (
    <Grid
      container
      spacing={2}
      sx={{ alignItems: { xs: 'center', sm: 'stretch' } }}
    >
      <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
        <Stack spacing={1.5}>
          {splat.type === 'completion' && (
            <DetailRow label="Completion requires">
              {completionRequiresDisplay(splat, splatIdToName)}
            </DetailRow>
          )}
          {splat.type === 'first_event_review' && (
            <DetailRow label="Event">
              {firstEventReviewEventDisplay(splat, eventIdToName)}
            </DetailRow>
          )}
          <DetailRow label="Beer journey map" multiline>
            {(splat.beerJourneyMap ?? '').trim() !== ''
              ? splat.beerJourneyMap
              : '—'}
          </DetailRow>
          <DetailRow label="Image">{splat.image ?? '—'}</DetailRow>
          <DetailRow label="Limit">{splat.limit ?? 0}</DetailRow>
          <DetailRow label="Trigger threshold">
            {triggerThresholdDisplay(splat)}
          </DetailRow>
          {formatUpdatedAt(splat) && (
            <DetailRow label="Updated">{formatUpdatedAt(splat)}</DetailRow>
          )}
        </Stack>
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        sx={{
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <DetailRow label="Name" multiline>
            {splat.name ?? '—'}
          </DetailRow>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 2,
              width: '100%',
            }}
          >
            <SplatImagePreview filename={imageName} url={previewUrl} />
            {(splat.overlayText ?? '').trim() !== '' && (
              <Box sx={{ flex: '1 1 0', minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                <DetailRow label="Overlay text" multiline>
                  {splat.overlayText}
                </DetailRow>
              </Box>
            )}
          </Box>
          <DetailRow label="Text" multiline>
            {splat.text ?? ''}
          </DetailRow>
        </Stack>
      </Grid>
    </Grid>
  );
}

function SplatDefinitionCard({
  splat,
  previewUrl,
  onEdit,
  onDelete,
  deleteDisabledReason,
  splatIdToName,
  eventIdToName,
}) {
  const imageName = (splat.image ?? '').trim();
  const handleEditClick = useCallback(() => {
    onEdit(splat);
  }, [onEdit, splat]);
  const handleDeleteClick = useCallback(() => {
    onDelete(splat);
  }, [onDelete, splat]);

  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <SplatCardHeaderRow
          splat={splat}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          deleteDisabledReason={deleteDisabledReason}
        />
        <Divider sx={{ my: 1.5 }} />
        <SplatCardDetailsGrid
          splat={splat}
          imageName={imageName}
          previewUrl={previewUrl}
          splatIdToName={splatIdToName}
          eventIdToName={eventIdToName}
        />
      </CardContent>
    </Card>
  );
}

/**
 * Card list of splat definitions with edit/delete actions (replaces a wide table on small viewports).
 */
function SplatsTable({
  splats,
  emptyMessage,
  splatIdToName = new Map(),
  eventIdToName = new Map(),
  imageUrlByName = {},
  onEdit,
  onDelete,
  canDeleteSplat = (_splat) => true,
  getDeleteDisabledReason,
}) {
  if (!splats.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage ?? 'No splat definitions yet. Add one to get started.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {splats.map((splat) => {
        const imageName = (splat.image ?? '').trim();
        const previewUrl = resolveStoredImageUrl(imageUrlByName, imageName);
        const deleteDisabledReason = getDeleteDisabledReason
          ? getDeleteDisabledReason(splat)
          : canDeleteSplat(splat)
            ? null
            : 'Cannot delete this splat.';

        return (
          <SplatDefinitionCard
            key={splat.id}
            splat={splat}
            previewUrl={previewUrl}
            onEdit={onEdit}
            onDelete={onDelete}
            deleteDisabledReason={deleteDisabledReason}
            splatIdToName={splatIdToName}
            eventIdToName={eventIdToName}
          />
        );
      })}
    </Stack>
  );
}

export default SplatsTable;
